import { ApolloCache } from '@apollo/client'
import { Entry, Obj, traverseAndCollectRefs } from './traverseAndCollectRefs'
import { groupBy } from '@sketch/utils'
import produce from 'immer'

function getAtPath(obj: Obj, pathParts: string[]): Obj | undefined {
  return pathParts.reduce((currentObj: Obj | undefined, field) => {
    if (currentObj) return currentObj[field] as Obj | undefined
    return undefined
  }, obj)
}

export type UpdaterFunction = (obj: Obj, info: Entry) => void

export interface UpdateCacheEntriesInput {
  cache: ApolloCache<any>
  // To determine which entries to traverse deeper into (used to short-circuit traversal)
  shouldTraverse: (entry: Entry) => boolean
  // To determine which entries to collect and return
  shouldCollect: (entry: Entry) => boolean
  updater: UpdaterFunction
}

const skipRootKeys = new Set([
  'ROOT_QUERY',
  'ROOT_MUTATION',
  'ROOT_SUBSCRIPTION',
])

export function updateCacheEntries(args: UpdateCacheEntriesInput) {
  const { cache, shouldTraverse, shouldCollect, updater } = args
  const normalizedCache = cache.extract()

  const pickedEntries = traverseAndCollectRefs({
    source: normalizedCache,
    shouldTraverse: entry =>
      '__typename' in entry.object &&
      !skipRootKeys.has(entry.path) &&
      shouldTraverse(entry),
    shouldCollect,
  })

  const objectFieldPairs = Array.from(pickedEntries.values())
    .filter(entry => entry.pathParts.length > 1)
    .map(entry => ({
      key: entry.pathParts[0] + '.' + entry.pathParts[1],
      rootObject: entry.parents[0],
      field: entry.pathParts[1],
      entry,
    }))

  const groupedByObjField = groupBy(objectFieldPairs, x => x.key)

  for (const group of Object.values(groupedByObjField)) {
    const { rootObject, field } = group[0]
    cache.modify({
      // rootObject is guaranteed to have __typename
      id: cache.identify(rootObject as { __typename: string }),
      fields: {
        [field]: existingFieldValue => {
          const updated = produce(existingFieldValue, draft => {
            for (const { entry } of group) {
              const objectToUpdate = getAtPath(
                draft as Obj,
                entry.pathParts.slice(2)
              )

              if (objectToUpdate) updater(objectToUpdate, entry)
            }
          })
          return updated
        },
      },
    })
  }
}
